/**
 * Payment Aging Card
 *
 * Displays payment aging breakdown with horizontal bar visualization.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { PaymentAging } from '../types';

interface PaymentAgingCardProps {
  data?: PaymentAging;
  loading?: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function PaymentAgingCard({ data, loading }: PaymentAgingCardProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Payment Aging</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="flex justify-between mb-1">
                  <div className="h-4 bg-muted rounded w-20" />
                  <div className="h-4 bg-muted rounded w-16" />
                </div>
                <div className="h-2 bg-muted rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const buckets = [
    { label: 'Current', data: data?.current, color: 'bg-green-500' },
    { label: '1-30 days', data: data?.days_1_to_30, color: 'bg-yellow-500' },
    { label: '31-60 days', data: data?.days_31_to_60, color: 'bg-orange-500' },
    { label: '61-90 days', data: data?.days_61_to_90, color: 'bg-red-400' },
    { label: '90+ days', data: data?.days_90_plus, color: 'bg-red-600' },
  ];

  const maxAmount = Math.max(
    ...buckets.map((b) => b.data?.amount ?? 0),
    1
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Payment Aging</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {buckets.map((bucket) => {
            const amount = bucket.data?.amount ?? 0;
            const count = bucket.data?.count ?? 0;
            const percentage = (amount / maxAmount) * 100;

            return (
              <div key={bucket.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">
                    {bucket.label}
                    {count > 0 && (
                      <span className="ml-1 text-xs">({count})</span>
                    )}
                  </span>
                  <span className="font-medium">{formatCurrency(amount)}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full ${bucket.color} rounded-full transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
